// src/utils/extractFiles.ts
//
// Parses assistant markdown for fenced code blocks and understands an extended
// info-string convention: ```language:filename.ext
// e.g. ```html:index.html   ```css:style.css   ```js:script.js
// Falls back gracefully for plain ```language blocks (no filename).

export interface PreviewFile {
  filename: string;
  language: string;
  code: string;
}

const FENCE_RE = /```([a-zA-Z0-9_+-]+)?(?::([^\s`\n]+))?\r?\n([\s\S]*?)```/g;

// These languages are rendered inline as diagrams/charts/SVG, or as a
// generated-file download card, by CodeBlock — not as downloadable source
// files in the traditional sense. Excluded from extractCodeFiles() so they
// don't get swept into the "N files generated / Download all" toolbar
// alongside real code. pptx-json/pdf-json specifically already have their
// own dedicated Download button (GenerateFileCard), so including them here
// would just offer a confusing second, wrong way to "download" raw JSON.
const NON_FILE_LANGS = new Set(['mermaid', 'chart-json', 'pptx-json', 'pdf-json', 'docx-json', 'xlsx-json']);

let anonCounter = 0;
function defaultFilename(language: string): string {
  const ext = (language || 'txt').toLowerCase();
  const map: Record<string, string> = {
    javascript: 'js', typescript: 'ts', python: 'py', markup: 'html',
    shell: 'sh', bash: 'sh', yaml: 'yml',
  };
  anonCounter += 1;
  return `snippet-${anonCounter}.${map[ext] || ext}`;
}

// Extracts every fenced code block in a message as a file. Blocks that share
// the same filename overwrite earlier ones (keeps the latest version if the
// model re-emits a file later in the same message).
export function extractCodeFiles(content: string): PreviewFile[] {
  if (!content || content.indexOf('```') === -1) return [];
  const filesByName = new Map<string, PreviewFile>();
  let match: RegExpExecArray | null;
  FENCE_RE.lastIndex = 0;
  while ((match = FENCE_RE.exec(content)) !== null) {
    const language = (match[1] || 'text').toLowerCase();
    if (NON_FILE_LANGS.has(language)) continue;
    const filename = match[2] || defaultFilename(language);
    const code = match[3].replace(/\n$/, '');
    if (!code.trim()) continue;
    filesByName.set(filename, { filename, language, code });
  }
  return Array.from(filesByName.values());
}

export interface GenerationStatus {
  filename?: string;
  language?: string;
  lineCount: number;
}

// While a message is still streaming, detects whether we're currently inside
// an *unclosed* code fence, and if so reports which file is being written and
// how many lines have landed so far — powers the "Writing index.html — 342
// lines…" live indicator.
export function getActiveGeneration(content: string): GenerationStatus | null {
  if (!content) return null;
  const parts = content.split('```');
  // Even number of parts => odd number of ``` markers => currently inside an
  // open fence (the last part is the in-progress block).
  if (parts.length % 2 !== 0) return null;
  const last = parts[parts.length - 1];
  const newlineIdx = last.indexOf('\n');
  const infoString = newlineIdx === -1 ? last : last.slice(0, newlineIdx);
  const body = newlineIdx === -1 ? '' : last.slice(newlineIdx + 1);
  const [langPart, filePart] = infoString.split(':');
  const lineCount = body.length ? body.split('\n').length : 0;
  return {
    filename: filePart?.trim() || undefined,
    language: langPart?.trim() || undefined,
    lineCount,
  };
}